import { PropsWithChildren } from "react";
import { useFormContext } from "react-hook-form";

import { OURS_EVENTS, trackOursEvent } from "~/providers";
import { ROUTES } from "~/router";
import { PatientScreeningFormSchema } from "~/services";
import { Button, ButtonProps, Title, useRedirect } from "~/shared";
import { useBookingFlowStore, usePatientStore } from "~/stores";
import { Input } from "~/ui";
import { tw } from "~/utils";

export const EMAIL_ERROR_MESSAGE = "Email is required.";
export const EMAIL_FORM_ID = "email-form";

type RootProps = {
  title: string;
  description?: string;
  className?: string;
};

const Root = ({
  className,
  children,
  title,
  description,
}: PropsWithChildren & RootProps) => {
  return (
    <div className={className}>
      <Title>{title}</Title>
      {description && (
        <p className="text-indigo-charcoal pb-3">{description}</p>
      )}
      {children}
    </div>
  );
};

const BaseButton = ({ children, ...rest }: ButtonProps) => {
  return (
    <Button size="default" type="button" className="w-full" {...rest}>
      {children}
    </Button>
  );
};

const Warning = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  return (
    <div
      className={tw(
        "bg-lightYellow text-dark-blue-gray rounded-md px-3 py-1.5 text-sm",
        className,
      )}
    >
      {children}
    </div>
  );
};

const AgreeToMarketing = () => {
  return (
    <p className="text-midnight-indigo text-[12px]">
      By entering your email, you agree to receive marketing materials from
      Elektra Health.
    </p>
  );
};

const Divider = () => {
  return <hr className="border-blueGrey border-[.5px]" />;
};

const LearnMore = () => {
  const redirect = useRedirect();

  return (
    <Step.Button
      onClick={() => {
        redirect("https://elektrahealth.com", { preserveHistory: true });
      }}
    >
      Learn more
    </Step.Button>
  );
};

const EmailInput = () => {
  const {
    register,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
  } = useFormContext<PatientScreeningFormSchema>();
  const updatePatientVisitData = usePatientStore(
    (state) => state.updatePatientVisitData,
  );

  const handleEmailEntry = () => {
    const value = getValues("email");
    if (value) {
      updatePatientVisitData({
        patientEmail: value,
      });

      trackOursEvent(OURS_EVENTS.EMAIL_ENTRY, {
        email: value,
      });
    } else {
      setError("email", { type: "required", message: EMAIL_ERROR_MESSAGE });
    }
  };

  return (
    <>
      <form
        id={EMAIL_FORM_ID}
        hidden
        onSubmit={(event) => event.preventDefault()}
      />
      <Input
        form={EMAIL_FORM_ID}
        id="email"
        required
        label="Email"
        {...register("email", {
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!event.target.value) {
              setError("email", {
                type: "required",
                message: EMAIL_ERROR_MESSAGE,
              });
            } else if (errors.email?.type === "required") {
              clearErrors("email");
            }
          },
        })}
        placeholder="example@email.com"
        autoCorrect="off"
        autoComplete="off"
        spellCheck="false"
        error={errors.email?.message}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;

          if (event.repeat) {
            event.preventDefault();
            return;
          }

          handleEmailEntry();
        }}
        onBlur={handleEmailEntry}
      />
    </>
  );
};

const MeetWithCoachButton = () => {
  const redirect = useRedirect();
  const clearBookingFlow = useBookingFlowStore((state) => state.clear);

  return (
    <Step.Button
      onClick={() => {
        redirect(ROUTES.hubMeetCoach);
        clearBookingFlow();
      }}
    >
      Meet with a coach
    </Step.Button>
  );
};

export const Step = {
  Root,
  Warning,
  AgreeToMarketing,
  Divider,
  LearnMore,
  EmailInput,
  Button: BaseButton,
  MeetWithCoachButton,
};
